import { useEffect } from 'react';

import { URLStore } from '../stores/url-store';
import type { HelpCategory, HelpQuestion } from '../api/help';

/**
 * Remove category ID from question URL for canonical purposes.
 * Handles both URL patterns:
 * - PATH: /{QUESTION_TITLE}-q-{QUESTION_ID}-c-{CATEGORY_ID} → /{QUESTION_TITLE}-q-{QUESTION_ID}
 * - QUERY_PARAM: ?question={ID}&category={ID}&title={TITLE} → ?question={ID}&title={TITLE}
 *
 * @param {string} url - question URL that may contain category ID
 * @returns {string} URL without category ID
 */
const removeCategoryFromUrl = (url: string): string => {
  // Handle PATH pattern: remove -c-{CATEGORY_ID} at the end
  const pathPattern = /-c-\d+$/;
  if (pathPattern.test(url)) {
    return url.replace(pathPattern, '');
  }

  // Handle QUERY_PARAM pattern: remove category={ID} parameter
  // Match &category=123 or category=123& or just category=123
  const queryPattern = /([?&])category=\d+(&?)/;
  if (queryPattern.test(url)) {
    return url.replace(queryPattern, (match, prefix, suffix) => {
      // If category was first param (?category=123&), keep the ?
      if (prefix === '?' && suffix === '&') {
        return '?';
      }
      // If category was in the middle (&category=123&), keep one &
      if (prefix === '&' && suffix === '&') {
        return '&';
      }
      // If category was last param (&category=123 or ?category=123), remove entirely
      return '';
    });
  }

  return url;
};

/**
 * Find or create link canonical element
 * @returns {HTMLLinkElement}
 */
const getOrCreateCanonical = (): HTMLLinkElement => {
  // get existing element
  const link = document.querySelector('link[rel=canonical]');

  if (link != null && link instanceof HTMLLinkElement) {
    return link;
  }

  // create new link element
  const element = document.createElement('link');
  element.rel = 'canonical';

  // add to document head
  const head = document.querySelector('head');
  if (head != null) {
    head.appendChild(element);
  }

  return element;
};

/**
 * Add canonical link to header of the page.
 *
 * Resolution order: selected question (category segment stripped),
 * otherwise the current category (URL used verbatim, it matches the
 * sitemap entry), otherwise the help root.
 *
 * @param {HelpQuestion|null} question - selected question for the canonical
 * @param {HelpCategory|null} category - current category, used when no question is selected
 * @param {boolean} isCanonicalActive - if false, does not overwrite existing canonical
 */
export const useCanonical = (
  question: HelpQuestion | null,
  category: HelpCategory | null,
  isCanonicalActive: boolean,
): void => {
  useEffect(() => {
    if (!isCanonicalActive) {
      // do not write canonical if disabled
      return;
    }
    // get element
    const element = getOrCreateCanonical();

    if (question != null) {
      // create path for the question (without category ID for canonical)
      element.href = URLStore.buildAbsolutePath(removeCategoryFromUrl(question.url));
    } else if (category != null) {
      // no question selected: canonical points to the category page.
      // the category URL is used verbatim — removeCategoryFromUrl would
      // strip the -c-{id} tail of a PATH pattern category URL
      element.href = URLStore.buildAbsolutePath(category.url);
    } else {
      // set canonical to base URL when nothing is selected (e.g. deleted question fallback)
      // this signals Google to consolidate stale question URLs into the base help page
      element.href = URLStore.buildAbsolutePath('');
    }

    // return cleanup function
    return () => {
      element.remove();
    };
  }, [question, category, isCanonicalActive]);
};
